import { useState } from 'react';
import { Send, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { useTemplatePublish } from '@/api/hooks/useTemplatePublish';

interface Props {
  templateId: string;
  tenantId: string;
  lifecycleState: string;
  sectionCount: number;
  questionCount: number;
  weightsBalanced: boolean;
  weightTotal: number;
  onPublished?: () => void;
}

export function PublishTemplateButton({
  templateId,
  tenantId,
  lifecycleState,
  sectionCount,
  questionCount,
  weightsBalanced,
  weightTotal,
  onPublished,
}: Props) {
  const [confirming, setConfirming] = useState(false);
  const { mutateAsync, isPending } = useTemplatePublish(templateId, tenantId);

  const canPublish = lifecycleState === 'draft' || lifecycleState === 'published';
  const isRepublish = lifecycleState === 'published';

  // S3-B: Publish is gated on a balanced weight total (frontend UX; the api enforces it too).
  const disabledReason =
    questionCount === 0
      ? 'Add at least one question before publishing'
      : !weightsBalanced
        ? `Section weights total ${weightTotal}% — must equal 100% before publishing`
        : undefined;

  if (!canPublish) return null;

  async function handlePublish() {
    setConfirming(false);
    try {
      await mutateAsync();
      toast.success(isRepublish ? 'New version published.' : 'Template published.');
      onPublished?.();
    } catch {
      toast.error('Publish failed. Please try again.');
    }
  }

  return (
    <>
      <Button
        size="sm"
        onClick={() => setConfirming(true)}
        disabled={isPending || questionCount === 0 || !weightsBalanced}
        title={disabledReason}
      >
        {isPending ? (
          <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
        ) : (
          <Send className="mr-1.5 h-3.5 w-3.5" />
        )}
        {isRepublish ? 'Publish New Version' : 'Publish Template'}
      </Button>

      {confirming && (
        <Dialog open onOpenChange={(open) => { if (!open) setConfirming(false); }}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
                <DialogTitle>
                  {isRepublish ? 'Publish New Version?' : 'Publish Template?'}
                </DialogTitle>
              </div>
            </DialogHeader>
            <div className="text-sm text-text-secondary space-y-2 py-1">
              <p>
                {isRepublish
                  ? 'A new version snapshot will be created. Assessments in progress will not be affected.'
                  : 'This template will become available for issuing assessments.'}
              </p>
              <p className="text-xs text-text-muted">
                {sectionCount} section{sectionCount !== 1 ? 's' : ''},{' '}
                {questionCount} question{questionCount !== 1 ? 's' : ''}
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirming(false)} disabled={isPending}>
                Cancel
              </Button>
              <Button onClick={handlePublish} disabled={isPending}>
                {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isRepublish ? 'Publish New Version' : 'Publish'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
